import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw, Home, Bug } from "lucide-react";
import { Button } from "@/ui/components/primitives/button";
import { Card } from "@/ui/components/primitives/card";
import { Badge } from "@/ui/components/primitives/badge";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  showRetry?: boolean;
  context?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  retryCount: number;
}

export class ErrorBoundary extends Component<Props, State> {
  private maxRetries = 3;

  public state: State = {
    hasError: false,
    retryCount: 0
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, retryCount: 0 };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Enhanced error logging with context
    const errorDetails = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      context: this.props.context || "Unknown",
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    };

    // Send error to monitoring service when available
    // @ts-ignore - monitoring service would be added here
    if (typeof window !== "undefined" && window.errorReportingService) {
      // @ts-ignore
      window.errorReportingService.captureException(error, errorDetails);
    }
  }

  private handleRetry = () => {
    if (this.state.retryCount < this.maxRetries) {
      this.setState(prevState => ({
        hasError: false,
        error: undefined,
        errorInfo: undefined,
        retryCount: prevState.retryCount + 1
      }));
    }
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="w-full max-w-lg p-6 text-center">
            {/* Error Icon */}
            <div className="flex justify-center mb-6">
              <AlertTriangle className="h-16 w-16 text-destructive" />
            </div>

            {/* Error Title */}
            <h1 className="text-2xl font-bold text-foreground mb-3">
              Application Error
            </h1>

            {/* Error Message */}
            <p className="text-muted-foreground mb-6 leading-relaxed">
              {this.state.error?.message || "An unexpected error occurred in PayGate."}
            </p>

            {/* Retry Information */}
            {this.state.retryCount > 0 && (
              <div className="mb-6">
                <Badge variant="outline" className="mb-2">
                  Retry Attempts: {this.state.retryCount}/{this.maxRetries}
                </Badge>
              </div>
            )}

            {/* Development Error Details */}
            {process.env.NODE_ENV === "development" && (
              <details className="mb-6 text-left bg-muted p-4 rounded-lg border">
                <summary className="cursor-pointer text-sm font-medium text-foreground mb-3 flex items-center">
                  <Bug className="h-4 w-4 mr-2" />
                  Error Details (Development)
                </summary>
                <div className="space-y-2 text-xs">
                  {this.props.context && (
                    <div>
                      <strong>Context:</strong> {this.props.context}
                    </div>
                  )}
                  <div>
                    <strong>Error:</strong> {this.state.error?.message}
                  </div>
                  <div>
                    <strong>Stack:</strong>
                  </div>
                  {this.state.error?.stack && (
                    <pre className="mt-1 overflow-auto bg-background p-2 rounded border max-h-48">
                      {this.state.error.stack}
                    </pre>
                  )}
                  {this.state.errorInfo?.componentStack && (
                    <div>
                      <strong>Component Stack:</strong>
                    </div>
                  )}
                  {this.state.errorInfo?.componentStack && (
                    <pre className="mt-1 overflow-auto bg-background p-2 rounded border max-h-32">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              </details>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              {this.props.showRetry && this.state.retryCount < this.maxRetries && (
                <Button
                  onClick={this.handleRetry}
                  className="w-full"
                  size="lg"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Try Again
                </Button>
              )}

              <Button
                variant="outline"
                onClick={() => window.location.reload()}
                className="w-full"
                size="lg"
              >
                Refresh Page
              </Button>

              <Button
                variant="ghost"
                onClick={() => window.location.href = "/dashboard"}
                className="w-full"
                size="sm"
              >
                <Home className="mr-2 h-4 w-4" />
                Back to Dashboard
              </Button>
            </div>

            {/* Support Information */}
            <div className="mt-6 pt-6 border-t">
              <p className="text-xs text-muted-foreground">
                If this error persists, please{" "}
                <a
                  href="mailto:support@paygate.sbs"
                  className="text-primary hover:underline"
                >
                  contact PayGate support
                </a>
                {" "}with details about what you were doing.
              </p>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}